import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '.'

export type IntegrationTestFlowStep =
  | 'idle' // Not in flow
  | 'selectRepository' // Pre-flow: Select repository modal (not counted in progress)
  | 'configureFlows' // Step 1: Choose & configure flows
  | 'deployment' // Step 2: Deployment configuration
  | 'review' // Step 3: Review and submit

export interface ExampleData {
  id: string
  input: string
  output: string
}

export interface SelectedFlow {
  id: string
  name: string
  needsTrigger: boolean
  needsScheduler?: boolean
  selected: boolean
  hasProvidedData?: boolean
  examples?: ExampleData[]
}

export interface SchemaFile {
  id: string
  name: string
  content: string
}

export interface ApplicationSchema {
  applicationId: string
  applicationName: string
  schemas: SchemaFile[]
}

export interface IntegrationTestFlowState {
  // Current step in the flow
  currentStep: IntegrationTestFlowStep

  // Repository information from step 1
  repository: {
    conn_type: string
    repository: string
    branch: string
    s3TarFilePath?: string // For S3 uploads
  } | null

  // Runtime settings from step 1
  runtimeSettings: Record<string, string | undefined>

  // Suggested runtime settings from GetRepoMetadata
  suggestedRuntimeSettings: {
    vmArgs?: string
    javaVersion?: string
    mvnVersion?: string
    minMuleRuntimeVersion?: string
  } | null

  // Selected flows from step 2
  selectedFlows: SelectedFlow[]
  autoGenerateTestData: boolean
  applicationSchemas: ApplicationSchema[] // Schemas organized by application
  additionalNotes?: string

  // Deployment configuration from step 3
  deploymentConfig: {
    deploymentOption: 'agent' | 'existing'
    vmArgs?: string
    deploymentUrl?: string
    javaVersion?: string
    mavenVersion?: string
    muleRuntimeVersion?: string
  } | null
}

export const initialState: IntegrationTestFlowState = {
  currentStep: 'idle',
  repository: null,
  runtimeSettings: {},
  suggestedRuntimeSettings: null,
  selectedFlows: [],
  autoGenerateTestData: true,
  applicationSchemas: [],
  deploymentConfig: null
}

export const integrationTestFlowSlice = createSlice({
  name: 'integrationTestFlow',
  initialState,
  reducers: {
    // Start the flow from repository selection
    startFlow(state) {
      state.currentStep = 'selectRepository'
    },

    // Complete repository selection and move to configure flows
    completeRepositorySelection(
      state,
      action: PayloadAction<{
        repository: {
          conn_type: string
          repository: string
          branch: string
          s3TarFilePath?: string
        }
        runtimeSettings?: Record<string, string | undefined>
      }>
    ) {
      state.repository = action.payload.repository
      state.runtimeSettings = action.payload.runtimeSettings || {}
      state.currentStep = 'configureFlows'
    },

    // Update flow selection
    updateFlowSelection(state, action: PayloadAction<{ flows: SelectedFlow[] }>) {
      state.selectedFlows = action.payload.flows
    },

    // Update auto-generate test data toggle
    setAutoGenerateTestData(state, action: PayloadAction<boolean>) {
      state.autoGenerateTestData = action.payload
    },

    // Initialize application schemas from API
    setApplicationSchemas(state, action: PayloadAction<ApplicationSchema[]>) {
      state.applicationSchemas = action.payload
    },

    // Add schema to a specific application
    addSchemaToApplication(
      state,
      action: PayloadAction<{ applicationId: string; schema: SchemaFile }>
    ) {
      const app = state.applicationSchemas.find(
        a => a.applicationId === action.payload.applicationId
      )
      if (app) {
        app.schemas.push(action.payload.schema)
      }
    },

    // Remove schema from a specific application
    removeSchemaFromApplication(
      state,
      action: PayloadAction<{ applicationId: string; schemaId: string }>
    ) {
      const app = state.applicationSchemas.find(
        a => a.applicationId === action.payload.applicationId
      )
      if (app) {
        app.schemas = app.schemas.filter(s => s.id !== action.payload.schemaId)
      }
    },

    // Clear all schemas from a specific application
    clearApplicationSchemas(state, action: PayloadAction<{ applicationId: string }>) {
      const app = state.applicationSchemas.find(
        a => a.applicationId === action.payload.applicationId
      )
      if (app) {
        app.schemas = []
      }
    },

    setAdditionalNotes(state, action: PayloadAction<string>) {
      state.additionalNotes = action.payload
    },

    // Update suggested runtime settings
    setSuggestedRuntimeSettings(
      state,
      action: PayloadAction<{
        vmArgs?: string
        javaVersion?: string
        mvnVersion?: string
        minMuleRuntimeVersion?: string
      }>
    ) {
      console.log('[Redux] setSuggestedRuntimeSettings called with:', action.payload)
      state.suggestedRuntimeSettings = action.payload
      console.log('[Redux] suggestedRuntimeSettings updated to:', state.suggestedRuntimeSettings)
    },

    // Complete flows configuration and move to deployment
    completeFlowsConfiguration(state) {
      state.currentStep = 'deployment'
    },

    // Update deployment configuration (without navigation)
    updateDeploymentConfig(
      state,
      action: PayloadAction<{
        deploymentOption: 'agent' | 'existing'
        vmArgs?: string
        deploymentUrl?: string
        javaVersion?: string
        mavenVersion?: string
        muleRuntimeVersion?: string
      }>
    ) {
      state.deploymentConfig = action.payload
    },

    // Complete deployment configuration and move to review
    completeDeploymentConfiguration(state) {
      state.currentStep = 'review'
    },

    // Navigate back to previous step
    goToPreviousStep(state) {
      switch (state.currentStep) {
        case 'configureFlows':
          state.currentStep = 'selectRepository'
          break
        case 'deployment':
          state.currentStep = 'configureFlows'
          break
        case 'review':
          state.currentStep = 'deployment'
          break
        default:
          break
      }
    },

    // Navigate to a specific step
    goToStep(state, action: PayloadAction<IntegrationTestFlowStep>) {
      state.currentStep = action.payload
    },

    // Reset the entire flow
    resetFlow(state) {
      return { ...initialState }
    },

    // Exit the flow (return to idle)
    exitFlow(state) {
      state.currentStep = 'idle'
    }
  }
})

export const {
  startFlow,
  completeRepositorySelection,
  updateFlowSelection,
  setAutoGenerateTestData,
  setApplicationSchemas,
  addSchemaToApplication,
  removeSchemaFromApplication,
  clearApplicationSchemas,
  setAdditionalNotes,
  setSuggestedRuntimeSettings,
  completeFlowsConfiguration,
  updateDeploymentConfig,
  completeDeploymentConfiguration,
  goToPreviousStep,
  goToStep,
  resetFlow,
  exitFlow
} = integrationTestFlowSlice.actions

// Selectors
export const selectIntegrationTestFlow = (state: RootState) => state.integrationTestFlow
export const selectCurrentStep = (state: RootState) => state.integrationTestFlow.currentStep
export const selectIsInFlow = (state: RootState) => state.integrationTestFlow.currentStep !== 'idle'
export const selectFlowRepository = (state: RootState) => state.integrationTestFlow.repository
export const selectSelectedFlows = (state: RootState) => state.integrationTestFlow.selectedFlows
export const selectAutoGenerateTestData = (state: RootState) =>
  state.integrationTestFlow.autoGenerateTestData
export const selectApplicationSchemas = (state: RootState) =>
  state.integrationTestFlow.applicationSchemas
export const selectAdditionalNotes = (state: RootState) => state.integrationTestFlow.additionalNotes
export const selectSuggestedRuntimeSettings = (state: RootState) =>
  state.integrationTestFlow.suggestedRuntimeSettings
export const selectDeploymentConfig = (state: RootState) =>
  state.integrationTestFlow.deploymentConfig

// Get current step number (1-indexed) for progress display
export const selectCurrentStepNumber = (state: RootState) => {
  const step = state.integrationTestFlow.currentStep
  switch (step) {
    case 'idle':
    case 'selectRepository':
      return 0 // Not in visible flow yet
    case 'configureFlows':
      return 1 // Step 1: Choose & configure flows
    case 'deployment':
      return 2 // Step 2: Deployment configuration
    case 'review':
      return 3 // Step 3: Review and submit
    default:
      return 0
  }
}

// Total number of steps in the flow
export const selectTotalSteps = () => 3

// Calculate progress percentage based on current step
export const selectProgressPercentage = (state: RootState) => {
  const step = state.integrationTestFlow.currentStep
  switch (step) {
    case 'idle':
    case 'selectRepository':
      return 0 // Not in flow yet
    case 'configureFlows':
      return 33.33 // Step 1 of 3
    case 'deployment':
      return 66.67 // Step 2 of 3
    case 'review':
      return 100 // Step 3 of 3
    default:
      return 0
  }
}

export default integrationTestFlowSlice.reducer
